import type { RhythmConfig } from '../rhythm/config.ts';
import type { ExplanationCard, LessonDefinition, MilestoneDefinition, RhythmLessonExerciseSpec, UnitDefinition } from './types.ts';

/**
 * Rhythm curriculum v1 (owner's outline, 2026-10-05). Six units:
 *   1. The pulse in 2/4, 4/4 and 2/2 — whole, half and quarter notes at ♩ = 60–70.
 *   2. Beat groupings — 3/4, 5/4 (3+2, 2+3), 6/4 (3+3, 2+2+2), 7/4 (4+3, 3+4, 2+3+2),
 *      with quarter, half, dotted half and whole notes.
 *   3. Dividing the beat — eighths, sixteenths, rests, dotted rhythms and ties, wider tempos.
 *   4. Triplets in simple meters.
 *   5. Compound meters — dotted-quarter and eighth-note tempo markings, and their accents.
 *   6. Everything together.
 * Each unit ends with a unit test: open from the start of the unit, so a learner
 * who already knows the material can pass it to skip ahead.
 *
 * Content ids are stable. To change a lesson, bump its `version`.
 */

export const RHYTHM_MILESTONES: readonly MilestoneDefinition[] = [
  { id: 'steady-pulse', title: 'Steady pulse', description: 'Passed the unit test on 2/4, 4/4 and 2/2.' },
  { id: 'beat-grouper', title: 'Beat grouper', description: 'Passed the unit test on 3/4, 5/4, 6/4 and 7/4 groupings.' },
  { id: 'subdivider', title: 'Subdivider', description: 'Passed the unit test on eighths, sixteenths, dots and ties.' },
  { id: 'triplet-tapper', title: 'Triplet tapper', description: 'Passed the unit test on triplets.' },
  { id: 'compound-counter', title: 'Compound counter', description: 'Passed the unit test on 6/8, 9/8 and 12/8.' },
  { id: 'rhythm-graduate', title: 'Rhythm graduate', description: 'Passed the final rhythm test.' },
];

type Part = Partial<RhythmConfig> & Pick<RhythmConfig, 'timeSignature'>;

/** A tap-the-rhythm part (fields in the validator's canonical form). */
function tap(p: Part): RhythmConfig {
  return { tempo: 66, measures: 2, noteValues: ['half', 'quarter'], dotted: false, rests: false, ties: false, triplets: false, questionCount: 4, ...p };
}

/** A tap-the-strong-beats part. */
function strong(p: Part): RhythmConfig {
  return tap({ noteValues: ['quarter'], questionCount: 3, mode: 'strong-beats', ...p });
}

const WHQ = ['whole', 'half', 'quarter'] as RhythmConfig['noteValues'];
const HQE = ['half', 'quarter', 'eighth'] as RhythmConfig['noteValues'];
const QE = ['quarter', 'eighth'] as RhythmConfig['noteValues'];
const QES = ['quarter', 'eighth', 'sixteenth'] as RhythmConfig['noteValues'];
const ALL = ['whole', 'half', 'quarter', 'eighth', 'sixteenth'] as RhythmConfig['noteValues'];

const TAPPING_CARD: ExplanationCard = {
  title: 'How rhythm practice works',
  body: 'Press Start: the metronome counts one measure in, then tap (or press Space) at the very start of every note. Listen plays the rhythm over the click first, as often as you like. Using wireless headphones? Run the timing calibration once so your taps are judged fairly.',
};

const STRONG_BEATS_CARD: ExplanationCard = {
  title: 'Tapping the strong beats',
  body: 'In “Tap the strong beats” the metronome clicks every beat the same, with no accents. Tap only the first beat of each group — the bold numbers — and feel the weaker beats in between without tapping.',
};

function unitTestCard(unit: string): ExplanationCard {
  return {
    title: 'Unit test',
    body: `A longer check that mixes everything in ${unit}. Pass with 80% on first tries to finish the unit. Already comfortable with this material? Take the test now — passing it marks the unit’s lessons as done.`,
  };
}

interface LessonInput {
  id: string;
  unitId: string;
  title: string;
  summary: string;
  prerequisites: string[];
  intro: ExplanationCard[];
  parts: RhythmConfig[];
  passAccuracy?: number;
  milestone?: string;
  placesLessonIds?: string[];
}

function lesson(input: LessonInput): LessonDefinition {
  const exercise: RhythmLessonExerciseSpec = { type: 'rhythm', parts: input.parts };
  return {
    id: input.id,
    version: 1,
    unitId: input.unitId,
    title: input.title,
    summary: input.summary,
    prerequisites: input.prerequisites,
    ...(input.milestone ? { milestone: input.milestone } : {}),
    ...(input.placesLessonIds ? { placesLessonIds: input.placesLessonIds } : {}),
    intro: input.intro,
    exercise,
    passAccuracy: input.passAccuracy ?? 0.7,
    skillKinds: [],
  };
}

/**
 * A unit's lessons in order: each requires the previous one; the first lesson
 * and the unit test both require only what the unit requires (so the test can
 * be taken early); the test is listed last and places the rest of the unit.
 */
function unit(
  meta: Omit<UnitDefinition, 'lessonIds' | 'curriculum'>,
  requires: string[],
  lessons: Omit<LessonInput, 'unitId' | 'prerequisites'>[],
  test: Omit<LessonInput, 'unitId' | 'prerequisites' | 'placesLessonIds'>,
): { unit: UnitDefinition; lessons: LessonDefinition[] } {
  const built = lessons.map((l, i) => lesson({ ...l, unitId: meta.id, prerequisites: i === 0 ? requires : [lessons[i - 1]!.id] }));
  const testLesson = lesson({ ...test, unitId: meta.id, prerequisites: requires, placesLessonIds: lessons.map((l) => l.id), passAccuracy: 0.8 });
  return { unit: { ...meta, curriculum: 'rhythm', lessonIds: [...built.map((l) => l.id), testLesson.id] }, lessons: [...built, testLesson] };
}

// ---------------------------------------------------------------------------
// Unit 1 — the pulse in 2/4, 4/4 and 2/2
// ---------------------------------------------------------------------------

const U1 = unit(
  { id: 'ru1-pulse', title: 'Feel the pulse', summary: 'The steady beat and its strong and weak pulses in 2/4, 4/4 and 2/2, with whole, half and quarter notes.' },
  [],
  [
    {
      id: 'rh-u1-steady-beat',
      title: 'The steady beat',
      summary: 'Tap quarter and half notes over a steady beat.',
      intro: [
        {
          title: 'Music has a heartbeat',
          body: 'Under almost every piece of music runs a steady pulse called the beat. Tempo is how fast the beat goes, in beats per minute: at 60, each beat lasts exactly one second. Listen, and count along out loud.',
          rhythmExample: { timeSignature: '4/4', tempo: 60, caption: 'A steady beat at ♩ = 60, four beats to each measure.' },
        },
        {
          title: 'Quarter, half and whole notes',
          body: 'In 4/4 a quarter note lasts one beat, a half note two beats and a whole note four. Tap once as each note begins, then keep counting while it lasts — the next tap comes when the next note starts.',
          rhythmExample: { timeSignature: '4/4', tempo: 60, rhythm: 'q q h | h q q', caption: 'Quarter, quarter, half — then half, quarter, quarter.' },
        },
        TAPPING_CARD,
      ],
      parts: [tap({ timeSignature: '4/4', tempo: 60, questionCount: 5 })],
    },
    {
      id: 'rh-u1-two-four',
      title: 'Strong and weak: 2/4',
      summary: 'Beats come in measures, and the first beat is the strongest.',
      intro: [
        {
          title: 'Reading a time signature',
          body: 'The top number says how many beats are in each measure; the bottom number says which note gets one beat (4 means a quarter note). So 2/4 has two quarter-note beats in every measure.',
        },
        {
          title: 'Strong, weak',
          body: 'The first beat of each measure — the downbeat — is felt the strongest. In 2/4 the pattern is strong–weak, strong–weak, like a march: LEFT-right, LEFT-right. Listen for the higher click on beat 1.',
          rhythmExample: { timeSignature: '2/4', tempo: 66, caption: '2/4: strong–weak in every measure.' },
        },
        STRONG_BEATS_CARD,
      ],
      parts: [strong({ timeSignature: '2/4' }), tap({ timeSignature: '2/4' })],
    },
    {
      id: 'rh-u1-four-four',
      title: 'Strong, weak, medium, weak: 4/4',
      summary: 'Four beats with two levels of accent.',
      intro: [
        {
          title: 'Four beats, two accents',
          body: '4/4 has four quarter-note beats. Beat 1 is the strongest, beat 3 is the next strongest, and beats 2 and 4 are weak: strong–weak–medium–weak. It’s the most common meter in music, so it’s often written with a C (common time).',
          rhythmExample: { timeSignature: '4/4', tempo: 66, caption: '4/4: strong–weak–medium–weak.' },
        },
        {
          title: 'Long notes sit on strong beats',
          body: 'Half notes usually start on beat 1 or beat 3, and a whole note on beat 1, so their sound lines up with the accents. Feeling the strong beats tells you where you are in the measure, even while a note is held.',
          rhythmExample: { timeSignature: '4/4', tempo: 66, rhythm: 'h h | q q h', caption: 'Half notes on beats 1 and 3, then quarter, quarter, half.' },
        },
      ],
      parts: [strong({ timeSignature: '4/4' }), tap({ timeSignature: '4/4', noteValues: WHQ, questionCount: 5 })],
    },
    {
      id: 'rh-u1-long-notes',
      title: 'Holding long notes',
      summary: 'Count through whole and half notes without rushing.',
      intro: [
        {
          title: 'Count while you hold',
          body: 'The hardest part of a long note is waiting. Count every beat in your head — “1, 2, 3, 4” through a whole note — and the next tap lands on the next downbeat, right with the strong click.',
          rhythmExample: { timeSignature: '4/4', tempo: 60, rhythm: 'w | h q q', caption: 'A whole note: tap once, count four.' },
        },
      ],
      parts: [tap({ timeSignature: '4/4', tempo: 60, noteValues: WHQ, questionCount: 6 })],
    },
    {
      id: 'rh-u1-cut-time',
      title: 'Cut time: 2/2',
      summary: 'Two half-note beats per measure.',
      intro: [
        {
          title: 'The half note gets the beat',
          body: 'In 2/2 — called cut time and often written ¢ — there are two beats in each measure and the half note gets the beat. It looks like 4/4 on the page, but it’s felt in two: strong–weak.',
          rhythmExample: { timeSignature: '2/2', tempo: 60, caption: '2/2: two half-note beats, strong–weak.' },
        },
        {
          title: 'Quarter notes in cut time',
          body: 'A quarter note is half a beat in 2/2, so two quarter notes share one beat, and a whole note fills the measure. At 𝅗𝅥 = 60, quarter notes go by twice a second.',
          rhythmExample: { timeSignature: '2/2', tempo: 60, rhythm: 'h q q | w', caption: 'Half, quarter, quarter — then a whole note.' },
        },
      ],
      parts: [strong({ timeSignature: '2/2', tempo: 60 }), tap({ timeSignature: '2/2', tempo: 60, noteValues: WHQ })],
    },
    {
      id: 'rh-u1-switching',
      title: 'Switching meters',
      summary: '2/4, 4/4 and 2/2 in one session.',
      intro: [
        {
          title: 'Same notes, different feel',
          body: 'A half note lasts two beats in 2/4 and 4/4 but only one in 2/2. Check the time signature and listen to the count-in before every question — it tells you how the measure is counted.',
        },
      ],
      parts: [
        tap({ timeSignature: '2/4', questionCount: 3 }),
        tap({ timeSignature: '4/4', noteValues: WHQ, questionCount: 3 }),
        tap({ timeSignature: '2/2', tempo: 60, noteValues: WHQ, questionCount: 3 }),
      ],
    },
  ],
  {
    id: 'rh-u1-test',
    title: 'Unit 1 test',
    summary: 'Strong beats and rhythms in 2/4, 4/4 and 2/2.',
    milestone: 'steady-pulse',
    intro: [unitTestCard('2/4, 4/4 and 2/2')],
    parts: [
      strong({ timeSignature: '4/4', questionCount: 2 }),
      tap({ timeSignature: '2/4', tempo: 70, questionCount: 3 }),
      tap({ timeSignature: '4/4', tempo: 70, noteValues: WHQ }),
      tap({ timeSignature: '2/2', tempo: 60, noteValues: WHQ, questionCount: 3 }),
    ],
  },
);

// ---------------------------------------------------------------------------
// Unit 2 — beat groupings in 3/4, 5/4, 6/4 and 7/4
// ---------------------------------------------------------------------------

const U2 = unit(
  {
    id: 'ru2-groupings',
    title: 'Beat groupings',
    summary: 'Waltz time and the uneven meters: 3/4, 5/4, 6/4 and 7/4, with quarter, half, dotted half and whole notes.',
  },
  ['rh-u1-test'],
  [
    {
      id: 'rh-u2-waltz',
      title: 'Waltz time: 3/4',
      summary: 'Strong–weak–weak, and the dotted half note.',
      intro: [
        {
          title: 'One strong, two weak',
          body: '3/4 has three quarter-note beats: strong–weak–weak. It’s the meter of the waltz — ONE two three, ONE two three — with a clear lift after every downbeat.',
          rhythmExample: { timeSignature: '3/4', tempo: 66, caption: '3/4: strong–weak–weak.' },
        },
        {
          title: 'The dotted half note',
          body: 'A dot after a note adds half of its value. A half note is two beats, so a dotted half note is three — one whole measure of 3/4.',
          rhythmExample: { timeSignature: '3/4', tempo: 66, rhythm: 'h. | q q q | h q | h.', caption: 'Dotted half, three quarters, half–quarter, dotted half.' },
        },
      ],
      parts: [strong({ timeSignature: '3/4' }), tap({ timeSignature: '3/4', dotted: true, questionCount: 5 })],
    },
    {
      id: 'rh-u2-five-three-two',
      title: '5/4 felt as 3+2',
      summary: 'Five beats in a group of three and a group of two.',
      intro: [
        {
          title: 'An uneven meter',
          body: 'Five beats can’t split into equal halves, so 5/4 groups them unevenly. Felt 3+2, the accents fall on beats 1 and 4: STRONG weak weak, MEDIUM weak — a measure of 3/4 followed by one of 2/4.',
          rhythmExample: { timeSignature: '5/4', grouping: [3, 2], tempo: 66, caption: '5/4 as 3+2: accents on 1 and 4.' },
        },
        {
          title: 'Notes follow the groups',
          body: 'Written rhythms show the grouping: a long note stays inside its group. In 3+2, a dotted half (beats 1–3) and then a half note (beats 4–5) fit exactly.',
          rhythmExample: { timeSignature: '5/4', grouping: [3, 2], tempo: 66, rhythm: 'h. h | q q q q q', caption: 'Dotted half + half, then five quarters.' },
        },
      ],
      parts: [strong({ timeSignature: '5/4', grouping: [3, 2] }), tap({ timeSignature: '5/4', grouping: [3, 2], dotted: true })],
    },
    {
      id: 'rh-u2-five-two-three',
      title: '5/4 felt as 2+3',
      summary: 'The same five beats, grouped the other way.',
      intro: [
        {
          title: 'Two, then three',
          body: 'The same five beats can be felt 2+3, with accents on beats 1 and 3: STRONG weak, MEDIUM weak weak. Music shows which grouping it uses through how its notes are written, or with “2+3” above the staff — this app shows it beside the time signature.',
          rhythmExample: { timeSignature: '5/4', grouping: [2, 3], tempo: 66, caption: '5/4 as 2+3: accents on 1 and 3.' },
        },
        {
          title: 'Hear the difference',
          body: 'Compare the half note then dotted half of 2+3 with the dotted half then half of 3+2. Same length, opposite feel.',
          rhythmExample: { timeSignature: '5/4', grouping: [2, 3], tempo: 66, rhythm: 'h h. | q q q q q', caption: 'Half + dotted half, then five quarters.' },
        },
      ],
      parts: [strong({ timeSignature: '5/4', grouping: [2, 3] }), tap({ timeSignature: '5/4', grouping: [2, 3], dotted: true })],
    },
    {
      id: 'rh-u2-six-four',
      title: '6/4: 3+3 or 2+2+2',
      summary: 'Six beats felt in two big pulses or three.',
      intro: [
        {
          title: 'Two big pulses',
          body: 'Felt 3+3, 6/4 has two big pulses, on beats 1 and 4 — like two measures of 3/4 joined together. Dotted half notes fit each half exactly.',
          rhythmExample: { timeSignature: '6/4', grouping: [3, 3], tempo: 66, caption: '6/4 as 3+3: accents on 1 and 4.' },
        },
        {
          title: 'Three smaller pulses',
          body: 'Felt 2+2+2, the accents fall on beats 1, 3 and 5 — like three measures of 2/4. Now half notes line up with the groups instead. Composers choose the grouping; you need to be ready for both.',
          rhythmExample: { timeSignature: '6/4', grouping: [2, 2, 2], tempo: 66, caption: '6/4 as 2+2+2: accents on 1, 3 and 5.' },
        },
      ],
      parts: [
        strong({ timeSignature: '6/4', grouping: [3, 3], questionCount: 2 }),
        strong({ timeSignature: '6/4', grouping: [2, 2, 2], questionCount: 2 }),
        tap({ timeSignature: '6/4', grouping: [3, 3], noteValues: WHQ, dotted: true, measures: 1, questionCount: 3 }),
        tap({ timeSignature: '6/4', grouping: [2, 2, 2], noteValues: WHQ, dotted: true, measures: 1, questionCount: 3 }),
      ],
    },
    {
      id: 'rh-u2-seven-four',
      title: '7/4: 4+3 and 3+4',
      summary: 'Seven beats in a long group and a short one.',
      intro: [
        {
          title: 'Seven beats',
          body: '7/4 usually splits into a group of four and a group of three. Felt 4+3, the accents land on beats 1 and 5; felt 3+4, on beats 1 and 4. Count the groups, not all seven numbers: “1 2 3 4, 1 2 3”.',
          rhythmExample: { timeSignature: '7/4', grouping: [4, 3], tempo: 66, caption: '7/4 as 4+3: accents on 1 and 5.' },
        },
        {
          title: 'Turned around',
          body: 'In 3+4 the short group comes first: “1 2 3, 1 2 3 4”.',
          rhythmExample: { timeSignature: '7/4', grouping: [3, 4], tempo: 66, caption: '7/4 as 3+4: accents on 1 and 4.' },
        },
      ],
      parts: [
        strong({ timeSignature: '7/4', grouping: [4, 3], questionCount: 2 }),
        strong({ timeSignature: '7/4', grouping: [3, 4], questionCount: 2 }),
        tap({ timeSignature: '7/4', grouping: [4, 3], noteValues: WHQ, dotted: true, measures: 1, questionCount: 3 }),
        tap({ timeSignature: '7/4', grouping: [3, 4], noteValues: WHQ, dotted: true, measures: 1, questionCount: 3 }),
      ],
    },
    {
      id: 'rh-u2-seven-two-three-two',
      title: '7/4 as 2+3+2',
      summary: 'Three groups in one measure.',
      intro: [
        {
          title: 'Short, long, short',
          body: '7/4 can also be felt in three groups, 2+3+2, with accents on beats 1, 3 and 6: “1 2, 1 2 3, 1 2”. The middle group is the long one — let it breathe.',
          rhythmExample: { timeSignature: '7/4', grouping: [2, 3, 2], tempo: 66, caption: '7/4 as 2+3+2: accents on 1, 3 and 6.' },
        },
      ],
      parts: [strong({ timeSignature: '7/4', grouping: [2, 3, 2] }), tap({ timeSignature: '7/4', grouping: [2, 3, 2], dotted: true, measures: 1 })],
    },
    {
      id: 'rh-u2-review',
      title: 'Groupings review',
      summary: '3/4, 5/4, 6/4 and 7/4 back to back.',
      intro: [
        {
          title: 'Read the grouping first',
          body: 'Before each question, look at the time signature and its grouping, and let the count-in set the accents in your ear. Then tap.',
        },
      ],
      parts: [
        tap({ timeSignature: '3/4', dotted: true, questionCount: 2 }),
        tap({ timeSignature: '5/4', grouping: [3, 2], dotted: true, questionCount: 2 }),
        tap({ timeSignature: '5/4', grouping: [2, 3], dotted: true, questionCount: 2 }),
        tap({ timeSignature: '6/4', grouping: [2, 2, 2], noteValues: WHQ, dotted: true, measures: 1, questionCount: 2 }),
        tap({ timeSignature: '7/4', grouping: [2, 3, 2], dotted: true, measures: 1, questionCount: 2 }),
      ],
    },
  ],
  {
    id: 'rh-u2-test',
    title: 'Unit 2 test',
    summary: 'Strong beats and rhythms in 3/4, 5/4, 6/4 and 7/4.',
    milestone: 'beat-grouper',
    intro: [unitTestCard('3/4, 5/4, 6/4 and 7/4')],
    parts: [
      strong({ timeSignature: '5/4', grouping: [2, 3], questionCount: 2 }),
      strong({ timeSignature: '7/4', grouping: [3, 4], questionCount: 2 }),
      tap({ timeSignature: '3/4', tempo: 70, dotted: true, questionCount: 2 }),
      tap({ timeSignature: '5/4', grouping: [3, 2], dotted: true, questionCount: 2 }),
      tap({ timeSignature: '6/4', grouping: [3, 3], noteValues: WHQ, dotted: true, measures: 1, questionCount: 2 }),
      tap({ timeSignature: '7/4', grouping: [2, 3, 2], dotted: true, measures: 1, questionCount: 2 }),
    ],
  },
);

// ---------------------------------------------------------------------------
// Unit 3 — dividing the beat
// ---------------------------------------------------------------------------

const U3 = unit(
  {
    id: 'ru3-dividing',
    title: 'Dividing the beat',
    summary: 'Eighth and sixteenth notes, rests, dotted rhythms and ties, at a wider range of tempos.',
  },
  ['rh-u2-test'],
  [
    {
      id: 'rh-u3-eighths',
      title: 'Eighth notes',
      summary: 'Two notes to a beat.',
      intro: [
        {
          title: 'Two to a beat',
          body: 'An eighth note is half a quarter note, so two fit in one beat. Count them “1 and 2 and”: the number lands on the beat, the “and” exactly halfway to the next one. Eighth notes are joined by a beam within each beat.',
          rhythmExample: { timeSignature: '2/4', tempo: 72, rhythm: 'e e q | e e e e', caption: 'Two eighths and a quarter, then four eighths.' },
        },
      ],
      parts: [tap({ timeSignature: '2/4', tempo: 72, noteValues: QE, questionCount: 3 }), tap({ timeSignature: '4/4', tempo: 72, noteValues: HQE })],
    },
    {
      id: 'rh-u3-rests',
      title: 'Rests',
      summary: 'Silences have a length too.',
      intro: [
        {
          title: 'Measured silence',
          body: 'A rest is a silence with an exact length: a quarter rest lasts one beat, a half rest two, an eighth rest half a beat. Don’t tap on a rest — keep counting through it so the next note comes in on time.',
          rhythmExample: { timeSignature: '4/4', tempo: 72, rhythm: 'q rq q q | h rh', caption: 'A quarter rest on beat 2, then a half rest on beats 3–4.' },
        },
      ],
      parts: [tap({ timeSignature: '4/4', tempo: 72, noteValues: HQE, rests: true, questionCount: 5 })],
    },
    {
      id: 'rh-u3-eighths-groupings',
      title: 'Eighths in 3/4 and 6/4',
      summary: 'Divided beats inside the groupings you know.',
      intro: [
        {
          title: 'Groups still matter',
          body: 'Eighth notes divide each beat, but the beats still group the way the meter says: in 3/4 the accent stays on 1; in 6/4 felt 2+2+2, on 1, 3 and 5.',
          rhythmExample: { timeSignature: '3/4', tempo: 76, rhythm: 'q e e q | e e e e q', caption: '3/4 with eighth notes.' },
        },
      ],
      parts: [
        tap({ timeSignature: '3/4', tempo: 76, noteValues: HQE, rests: true, questionCount: 3 }),
        tap({ timeSignature: '6/4', grouping: [2, 2, 2], tempo: 76, noteValues: HQE, rests: true, measures: 1, questionCount: 3 }),
      ],
    },
    {
      id: 'rh-u3-sixteenths',
      title: 'Sixteenth notes',
      summary: 'Four notes to a beat.',
      intro: [
        {
          title: 'Four to a beat',
          body: 'A sixteenth note is half an eighth: four fit in one beat, counted “1 e and a”. Keep them even — the steady beat underneath matters even more when the notes get quick.',
          rhythmExample: { timeSignature: '2/4', tempo: 60, rhythm: 's s s s q | e s s e e', caption: 'Four sixteenths and a quarter, then eighth–two sixteenths, two eighths.' },
        },
      ],
      parts: [tap({ timeSignature: '2/4', tempo: 60, noteValues: QES, questionCount: 3 }), tap({ timeSignature: '4/4', tempo: 60, noteValues: QES })],
    },
    {
      id: 'rh-u3-dotted-quarter',
      title: 'Dotted quarter and eighth',
      summary: 'Long–short across the beat.',
      intro: [
        {
          title: 'A beat and a half',
          body: 'A dotted quarter lasts a beat and a half, and an eighth note fills out the second beat: count “1 (2) and”. The eighth comes on the “and” of beat 2 — feel the beat you don’t tap.',
          rhythmExample: { timeSignature: '4/4', tempo: 72, rhythm: 'q. e q q | q. e h', caption: 'Dotted quarter–eighth, two quarters; dotted quarter–eighth, half.' },
        },
      ],
      parts: [tap({ timeSignature: '4/4', tempo: 72, noteValues: HQE, dotted: true, questionCount: 5 })],
    },
    {
      id: 'rh-u3-dotted-eighth',
      title: 'Dotted eighth and sixteenth',
      summary: 'Long–short inside one beat.',
      intro: [
        {
          title: 'Three quarters and a quarter of a beat',
          body: 'A dotted eighth takes three sixteenths of the beat and the sixteenth after it takes the last one: “1 . . a”. It should snap — long, then very short — not sound like two even eighths.',
          rhythmExample: { timeSignature: '2/4', tempo: 60, rhythm: 'e. s e. s | q q', caption: 'Two dotted-eighth–sixteenth beats, then two quarters.' },
        },
      ],
      parts: [tap({ timeSignature: '2/4', tempo: 60, noteValues: QES, dotted: true }), tap({ timeSignature: '4/4', tempo: 60, noteValues: QES, dotted: true, questionCount: 3 })],
    },
    {
      id: 'rh-u3-ties',
      title: 'Ties across the beat',
      summary: 'One sound written as two notes.',
      intro: [
        {
          title: 'A tie joins two notes',
          body: 'A tie is a curved line joining two notes into one longer sound. Tap the first note only; the tied note just continues it. Ties let a sound start off the beat and carry across it — that’s syncopation.',
          rhythmExample: { timeSignature: '4/4', tempo: 72, rhythm: 'q e e~ e e q | w', caption: 'The eighth on the “and” of 2 is tied over beat 3.' },
        },
      ],
      parts: [tap({ timeSignature: '4/4', tempo: 72, noteValues: HQE, rests: true, ties: true, questionCount: 5 })],
    },
    {
      id: 'rh-u3-ties-barline',
      title: 'Ties over the barline',
      summary: 'Sounds that carry into the next measure.',
      intro: [
        {
          title: 'Into the next measure',
          body: 'A note can’t cross a barline on its own, so a sound that carries on is tied to a note in the next measure. Hold through the downbeat without tapping — the strong beat is felt, not played.',
          rhythmExample: { timeSignature: '3/4', tempo: 76, rhythm: 'q h~ | q q q', caption: 'A half note tied over the barline into beat 1.' },
        },
      ],
      parts: [
        tap({ timeSignature: '3/4', tempo: 76, noteValues: HQE, ties: true, questionCount: 3 }),
        tap({ timeSignature: '2/4', tempo: 76, noteValues: HQE, ties: true, questionCount: 3 }),
      ],
    },
    {
      id: 'rh-u3-odd-meters',
      title: 'Divided beats in 5/4 and 7/4',
      summary: 'Eighths, dots and rests in uneven meters.',
      intro: [
        {
          title: 'Keep the groups',
          body: 'With eighth notes in 5/4 or 7/4, beams still follow each beat, and the accents still follow the grouping. Count the groups — “1 and 2 and, 1 and 2 and 3 and” in 5/4 felt 2+3.',
        },
      ],
      parts: [
        tap({ timeSignature: '5/4', grouping: [2, 3], tempo: 80, noteValues: HQE, rests: true, dotted: true, measures: 1, questionCount: 3 }),
        tap({ timeSignature: '7/4', grouping: [2, 3, 2], tempo: 80, noteValues: HQE, rests: true, measures: 1, questionCount: 3 }),
      ],
    },
    {
      id: 'rh-u3-faster',
      title: 'Up to tempo',
      summary: 'Everything so far, faster.',
      intro: [
        {
          title: 'Faster, not rushed',
          body: 'At quicker tempos, aim to stay relaxed and let the beat carry you. If a rhythm feels tight, listen to it first, then tap along in your head before pressing Start.',
        },
      ],
      parts: [
        tap({ timeSignature: '4/4', tempo: 96, noteValues: ALL, rests: true, dotted: true, ties: true, questionCount: 3 }),
        tap({ timeSignature: '2/2', tempo: 80, noteValues: HQE, rests: true, questionCount: 3 }),
      ],
    },
  ],
  {
    id: 'rh-u3-test',
    title: 'Unit 3 test',
    summary: 'Eighths, sixteenths, rests, dots and ties across the meters.',
    milestone: 'subdivider',
    intro: [unitTestCard('eighths, sixteenths, rests, dotted rhythms and ties')],
    parts: [
      tap({ timeSignature: '2/4', tempo: 66, noteValues: QES, dotted: true, questionCount: 2 }),
      tap({ timeSignature: '3/4', tempo: 84, noteValues: HQE, rests: true, ties: true, questionCount: 2 }),
      tap({ timeSignature: '4/4', tempo: 90, noteValues: ALL, rests: true, dotted: true, ties: true, questionCount: 3 }),
      tap({ timeSignature: '5/4', grouping: [3, 2], tempo: 80, noteValues: HQE, rests: true, measures: 1, questionCount: 2 }),
      tap({ timeSignature: '6/4', grouping: [3, 3], tempo: 76, noteValues: HQE, dotted: true, measures: 1, questionCount: 2 }),
      tap({ timeSignature: '2/2', tempo: 80, noteValues: HQE, questionCount: 1 }),
    ],
  },
);

// ---------------------------------------------------------------------------
// Unit 4 — triplets in simple meters
// ---------------------------------------------------------------------------

const TRIPLETS = { noteValues: QE, triplets: true };

const U4 = unit(
  { id: 'ru4-triplets', title: 'Triplets', summary: 'Three notes in the time of two, in simple meters.' },
  ['rh-u3-test'],
  [
    {
      id: 'rh-u4-eighth-triplets',
      title: 'Three in the time of two',
      summary: 'Eighth-note triplets.',
      intro: [
        {
          title: 'What a triplet is',
          body: 'A triplet fits three equal notes into the time normally taken by two. Three eighth-note triplets fill one beat, so the beat splits in three instead of two. It’s marked with a small 3 over the group. Count “1-trip-let, 2-trip-let”.',
          rhythmExample: { timeSignature: '2/4', tempo: 60, rhythm: '3[e e e] q | 3[e e e] 3[e e e]', caption: 'A triplet and a quarter, then two triplets.' },
        },
      ],
      parts: [tap({ timeSignature: '2/4', tempo: 60, ...TRIPLETS }), tap({ timeSignature: '4/4', tempo: 60, ...TRIPLETS, questionCount: 3 })],
    },
    {
      id: 'rh-u4-triplets-eighths',
      title: 'Triplets or eighths?',
      summary: 'Switching between two and three per beat.',
      intro: [
        {
          title: 'Two or three',
          body: 'Two even eighths and three triplet eighths feel very different over the same beat. Look for the 3 over the beam, and let the count-in fix the beat before you switch between them.',
          rhythmExample: { timeSignature: '4/4', tempo: 66, rhythm: 'e e 3[e e e] e e q', caption: 'Eighths, a triplet, eighths, a quarter.' },
        },
      ],
      parts: [tap({ timeSignature: '4/4', tempo: 66, ...TRIPLETS, questionCount: 6 })],
    },
    {
      id: 'rh-u4-triplet-rests',
      title: 'Rests inside triplets',
      summary: 'Silences on part of a triplet.',
      intro: [
        {
          title: 'Keep counting the three',
          body: 'A triplet can have a rest in it. Keep all three parts of the beat going in your head — “1-trip-let” — and tap only the parts that sound.',
          rhythmExample: { timeSignature: '2/4', tempo: 60, rhythm: '3[e re e] q | 3[e e e] q', caption: 'A triplet with a rest in the middle.' },
        },
      ],
      parts: [tap({ timeSignature: '2/4', tempo: 60, ...TRIPLETS, rests: true, questionCount: 3 }), tap({ timeSignature: '3/4', tempo: 60, ...TRIPLETS, rests: true, questionCount: 3 })],
    },
    {
      id: 'rh-u4-long-short-triplets',
      title: 'Long–short triplets',
      summary: 'Quarter and eighth inside a triplet.',
      intro: [
        {
          title: 'Two parts joined',
          body: 'Inside a triplet, a quarter note takes two of the three parts. A quarter–eighth triplet gives a lilting long–short “1 — let” that’s the backbone of swing and shuffle rhythms.',
          rhythmExample: { timeSignature: '2/4', tempo: 66, rhythm: '3[q e] 3[q e] | 3[e e e] q', caption: 'Two long–short triplets, then a triplet and a quarter.' },
        },
      ],
      parts: [tap({ timeSignature: '4/4', tempo: 66, ...TRIPLETS, questionCount: 5 })],
    },
    {
      id: 'rh-u4-quarter-triplets',
      title: 'Quarter-note triplets',
      summary: 'Three notes across two beats.',
      intro: [
        {
          title: 'Across the beat',
          body: 'Three quarter-note triplets fill two beats, so only the first lands on a beat — the second and third fall between beats. In cut time (2/2) a quarter-note triplet fills one half-note beat, which makes it easier to feel.',
          rhythmExample: { timeSignature: '2/2', tempo: 60, rhythm: '3[q q q] h | 3[q q q] 3[q q q]', caption: 'Quarter-note triplets in cut time.' },
        },
      ],
      parts: [
        tap({ timeSignature: '2/2', tempo: 60, noteValues: ['half', 'quarter'], triplets: true }),
        tap({ timeSignature: '4/4', tempo: 60, noteValues: ['half', 'quarter'], triplets: true, questionCount: 3 }),
      ],
    },
    {
      id: 'rh-u4-triplet-meters',
      title: 'Triplets in 3/4, 5/4 and 7/4',
      summary: 'Triplets inside the groupings.',
      intro: [
        {
          title: 'Same triplet, new meter',
          body: 'A triplet always divides one beat (or, for quarter-note triplets, two). The meter around it doesn’t change that — keep the grouping’s accents and fit the triplets inside.',
        },
      ],
      parts: [
        tap({ timeSignature: '3/4', tempo: 66, ...TRIPLETS, questionCount: 2 }),
        tap({ timeSignature: '5/4', grouping: [3, 2], tempo: 66, ...TRIPLETS, measures: 1, questionCount: 2 }),
        tap({ timeSignature: '7/4', grouping: [4, 3], tempo: 66, ...TRIPLETS, measures: 1, questionCount: 2 }),
      ],
    },
  ],
  {
    id: 'rh-u4-test',
    title: 'Unit 4 test',
    summary: 'Eighth- and quarter-note triplets across simple meters.',
    milestone: 'triplet-tapper',
    intro: [unitTestCard('triplets')],
    parts: [
      tap({ timeSignature: '2/4', tempo: 66, ...TRIPLETS, questionCount: 3 }),
      tap({ timeSignature: '4/4', tempo: 66, ...TRIPLETS, rests: true, questionCount: 3 }),
      tap({ timeSignature: '2/2', tempo: 60, noteValues: ['half', 'quarter'], triplets: true, questionCount: 2 }),
      tap({ timeSignature: '3/4', tempo: 66, ...TRIPLETS, questionCount: 2 }),
      tap({ timeSignature: '5/4', grouping: [2, 3], tempo: 66, ...TRIPLETS, measures: 1, questionCount: 2 }),
    ],
  },
);

// ---------------------------------------------------------------------------
// Unit 5 — compound meters
// ---------------------------------------------------------------------------

const U5 = unit(
  {
    id: 'ru5-compound',
    title: 'Compound meters',
    summary: '6/8, 9/8 and 12/8: beats that divide in three, their accents, and tempo marked in dotted quarters or eighths.',
  },
  ['rh-u4-test'],
  [
    {
      id: 'rh-u5-six-eight',
      title: '6/8: two big beats',
      summary: 'Six eighth notes in two groups of three.',
      intro: [
        {
          title: 'Beats that divide in three',
          body: 'In a compound meter each beat is a dotted quarter, which divides into three eighth notes. 6/8 has six eighths grouped 3+3: two big beats in every measure, STRONG–weak, each with three eighths inside.',
          rhythmExample: { timeSignature: '6/8', countUnit: 'eighth', tempo: 50, caption: '6/8 counted in eighths: accents on 1 and 4.' },
        },
        {
          title: 'Two ways to mark the tempo',
          body: 'Compound tempos are usually marked with the dotted quarter: ♩. = 60 means 60 big beats a minute, felt “in two”. Slow music is often marked in eighths instead: ♪ = 150 means you count all six eighths. Both describe the same music; they tell you which pulse to feel.',
        },
        STRONG_BEATS_CARD,
      ],
      parts: [
        strong({ timeSignature: '6/8', countUnit: 'eighth', tempo: 50 }),
        tap({ timeSignature: '6/8', tempo: 60, noteValues: QE }),
      ],
    },
    {
      id: 'rh-u5-counting-in-six',
      title: 'Counting 6/8 in six',
      summary: 'Slow 6/8 with the eighth note as the pulse.',
      intro: [
        {
          title: 'Felt in six',
          body: 'At a slow tempo, 6/8 is counted 1-2-3-4-5-6 with the metronome on every eighth, the strongest accent on 1 and a lighter one on 4. A quarter note here lasts two eighths and a dotted quarter three.',
          rhythmExample: { timeSignature: '6/8', countUnit: 'eighth', tempo: 45, rhythm: 'q e q e | q. q.', caption: 'At ♪ = 135: quarter–eighth twice, then two dotted quarters.' },
        },
      ],
      parts: [tap({ timeSignature: '6/8', countUnit: 'eighth', tempo: 45, noteValues: QE, rests: true, questionCount: 5 })],
    },
    {
      id: 'rh-u5-six-eight-rhythms',
      title: 'Rhythms in 6/8',
      summary: 'Felt in two, at ♩. = 60.',
      intro: [
        {
          title: 'Back to two',
          body: 'At a moderate tempo, feel 6/8 in two big beats again. Quarter–eighth inside a beat gives the gently rocking “long–short” that 6/8 is known for.',
          rhythmExample: { timeSignature: '6/8', tempo: 60, rhythm: 'q e e e e | q. q e', caption: 'Quarter–eighth, three eighths; dotted quarter, quarter–eighth.' },
        },
      ],
      parts: [tap({ timeSignature: '6/8', tempo: 60, noteValues: ['half', 'quarter', 'eighth'], rests: true, questionCount: 5 })],
    },
    {
      id: 'rh-u5-nine-eight',
      title: '9/8: three big beats',
      summary: 'Nine eighths in three groups.',
      intro: [
        {
          title: 'Like 3/4, split in three',
          body: '9/8 has nine eighth notes in three groups: three dotted-quarter beats, STRONG–weak–weak — like 3/4 with every beat divided in three. Counted in eighths, the accents fall on 1, 4 and 7.',
          rhythmExample: { timeSignature: '9/8', countUnit: 'eighth', tempo: 40, caption: '9/8 counted in eighths: beats on 1, 4 and 7.' },
        },
      ],
      parts: [strong({ timeSignature: '9/8', countUnit: 'eighth', tempo: 40, measures: 1 }), tap({ timeSignature: '9/8', tempo: 56, noteValues: QE, measures: 1 })],
    },
    {
      id: 'rh-u5-twelve-eight',
      title: '12/8: four big beats',
      summary: 'Strong–weak–medium–weak, in threes.',
      intro: [
        {
          title: 'Like 4/4, in threes',
          body: '12/8 has four dotted-quarter beats with the same accents as 4/4 — strong–weak–medium–weak — each beat holding three eighths. It’s the rolling feel of many blues songs and slow ballads.',
          rhythmExample: { timeSignature: '12/8', tempo: 50, caption: '12/8: four big beats, strong–weak–medium–weak.' },
        },
      ],
      parts: [strong({ timeSignature: '12/8', tempo: 50, measures: 1 }), tap({ timeSignature: '12/8', tempo: 50, noteValues: QE, measures: 1 })],
    },
    {
      id: 'rh-u5-compound-sixteenths',
      title: 'Sixteenths and dots in compound time',
      summary: 'Dividing the eighth note.',
      intro: [
        {
          title: 'Smaller still',
          body: 'Each eighth in a compound beat can split into two sixteenths. A dotted eighth–sixteenth–eighth gives the skipping “jig” rhythm.',
          rhythmExample: { timeSignature: '6/8', tempo: 50, rhythm: 'e. s e e e e | s s e e q.', caption: 'A dotted figure, three eighths… then sixteenths.' },
        },
      ],
      parts: [tap({ timeSignature: '6/8', tempo: 50, noteValues: QES, dotted: true, questionCount: 5 })],
    },
    {
      id: 'rh-u5-compound-ties',
      title: 'Ties and rests in compound time',
      summary: '9/8 and 12/8 with held and silent beats.',
      intro: [
        {
          title: 'Hold through the big beat',
          body: 'Ties and rests work the same way in compound time. Keep the three eighths of every beat ticking in your head, so held sounds and silences end in the right place.',
        },
      ],
      parts: [
        tap({ timeSignature: '9/8', tempo: 52, noteValues: QE, rests: true, ties: true, measures: 1, questionCount: 3 }),
        tap({ timeSignature: '12/8', tempo: 52, noteValues: QE, rests: true, ties: true, measures: 1, questionCount: 3 }),
      ],
    },
    {
      id: 'rh-u5-six-eight-three-four',
      title: '6/8 or 3/4?',
      summary: 'Six eighths, grouped two different ways.',
      intro: [
        {
          title: 'Same eighths, different beats',
          body: '6/8 and 3/4 both hold six eighth notes, but 6/8 groups them 3+3 (two beats) and 3/4 groups them 2+2+2 (three beats). The beams show it: three eighths beamed together in 6/8, pairs in 3/4.',
          rhythmExample: { timeSignature: '6/8', tempo: 60, rhythm: 'e e e e e e', caption: '6/8: two groups of three.' },
        },
        {
          title: 'Hear it',
          body: 'Here the eighth notes move at the same speed as in 6/8 at ♩. = 60, but the accents fall every two eighths.',
          rhythmExample: { timeSignature: '3/4', tempo: 90, rhythm: 'e e e e e e', caption: '3/4: three groups of two.' },
        },
      ],
      parts: [tap({ timeSignature: '6/8', tempo: 60, noteValues: QE, questionCount: 3 }), tap({ timeSignature: '3/4', tempo: 90, noteValues: QE, questionCount: 3 })],
    },
  ],
  {
    id: 'rh-u5-test',
    title: 'Unit 5 test',
    summary: '6/8, 9/8 and 12/8, felt in beats and in eighths.',
    milestone: 'compound-counter',
    intro: [unitTestCard('6/8, 9/8 and 12/8')],
    parts: [
      strong({ timeSignature: '6/8', countUnit: 'eighth', tempo: 50, questionCount: 2 }),
      tap({ timeSignature: '6/8', countUnit: 'eighth', tempo: 45, noteValues: QE, rests: true, questionCount: 2 }),
      tap({ timeSignature: '6/8', tempo: 60, noteValues: QES, dotted: true, questionCount: 2 }),
      tap({ timeSignature: '9/8', tempo: 56, noteValues: QE, rests: true, measures: 1, questionCount: 2 }),
      tap({ timeSignature: '12/8', tempo: 50, noteValues: QE, ties: true, measures: 1, questionCount: 2 }),
      tap({ timeSignature: '3/4', tempo: 90, noteValues: QE, questionCount: 2 }),
    ],
  },
);

// ---------------------------------------------------------------------------
// Unit 6 — everything together
// ---------------------------------------------------------------------------

const EVERYTHING = { noteValues: ALL, rests: true, dotted: true, ties: true };

const U6 = unit(
  { id: 'ru6-together', title: 'Putting it all together', summary: 'Every meter, grouping and rhythm from the path, mixed and up to tempo.' },
  ['rh-u5-test'],
  [
    {
      id: 'rh-u6-simple',
      title: 'Simple meters, everything',
      summary: '2/4, 3/4 and 4/4 with every rhythm.',
      intro: [{ title: 'Everything at once', body: 'Every note value, rest, dot and tie, in the simple meters. Read ahead: find the strong beats in each measure before you start.' }],
      parts: [
        tap({ timeSignature: '2/4', tempo: 80, ...EVERYTHING, questionCount: 2 }),
        tap({ timeSignature: '3/4', tempo: 80, ...EVERYTHING, questionCount: 2 }),
        tap({ timeSignature: '4/4', tempo: 84, ...EVERYTHING, questionCount: 3 }),
      ],
    },
    {
      id: 'rh-u6-uneven',
      title: 'Uneven meters, everything',
      summary: '5/4, 6/4 and 7/4 with every rhythm.',
      intro: [{ title: 'Groups first', body: 'Check each grouping before you start — it tells you where the accents and the long notes go.' }],
      parts: [
        tap({ timeSignature: '5/4', grouping: [2, 3], tempo: 76, ...EVERYTHING, measures: 1, questionCount: 2 }),
        tap({ timeSignature: '6/4', grouping: [3, 3], tempo: 76, ...EVERYTHING, measures: 1, questionCount: 2 }),
        tap({ timeSignature: '7/4', grouping: [2, 3, 2], tempo: 76, ...EVERYTHING, measures: 1, questionCount: 2 }),
      ],
    },
    {
      id: 'rh-u6-triplets',
      title: 'Triplets with everything',
      summary: 'Triplets mixed with dots, rests and ties.',
      intro: [{ title: 'Two and three together', body: 'Triplets now sit beside dotted rhythms and sixteenths. Keep the beat steady and let each beat divide its own way.' }],
      parts: [
        tap({ timeSignature: '4/4', tempo: 72, ...EVERYTHING, triplets: true, questionCount: 4 }),
        tap({ timeSignature: '2/2', tempo: 60, noteValues: ['half', 'quarter', 'eighth'], rests: true, triplets: true, questionCount: 2 }),
      ],
    },
    {
      id: 'rh-u6-compound',
      title: 'Compound meters, everything',
      summary: '6/8, 9/8 and 12/8 with every rhythm.',
      intro: [{ title: 'In threes', body: 'All the compound meters with dots, rests and ties. Feel the big beats; count eighths only when a tempo is marked in eighths.' }],
      parts: [
        tap({ timeSignature: '6/8', tempo: 66, noteValues: QES, rests: true, dotted: true, ties: true, questionCount: 2 }),
        tap({ timeSignature: '9/8', tempo: 56, noteValues: QES, rests: true, ties: true, measures: 1, questionCount: 2 }),
        tap({ timeSignature: '12/8', tempo: 52, noteValues: QE, rests: true, dotted: true, ties: true, measures: 1, questionCount: 2 }),
      ],
    },
    {
      id: 'rh-u6-switching',
      title: 'Switching meters',
      summary: 'A different meter every question or two.',
      intro: [{ title: 'Stay alert', body: 'Simple, compound and uneven meters back to back. The count-in is your friend: it plays one full measure with the new meter’s accents.' }],
      parts: [
        tap({ timeSignature: '3/4', tempo: 84, noteValues: HQE, rests: true, questionCount: 2 }),
        tap({ timeSignature: '6/8', tempo: 60, noteValues: QE, rests: true, questionCount: 2 }),
        tap({ timeSignature: '2/2', tempo: 72, noteValues: HQE, questionCount: 2 }),
        tap({ timeSignature: '12/8', tempo: 50, noteValues: QE, measures: 1, questionCount: 2 }),
        tap({ timeSignature: '7/4', grouping: [4, 3], tempo: 76, noteValues: HQE, measures: 1, questionCount: 2 }),
      ],
    },
    {
      id: 'rh-u6-faster',
      title: 'Faster tempos',
      summary: 'The same rhythms, quicker.',
      intro: [{ title: 'Push the tempo', body: 'Quick tempos test how steady your inner beat is. Stay loose, breathe, and trust the count.' }],
      parts: [
        tap({ timeSignature: '4/4', tempo: 112, noteValues: HQE, rests: true, dotted: true, ties: true, questionCount: 3 }),
        tap({ timeSignature: '6/8', tempo: 84, noteValues: QE, rests: true, questionCount: 3 }),
      ],
    },
  ],
  {
    id: 'rh-u6-final',
    title: 'Final rhythm test',
    summary: 'Everything on the rhythm path.',
    milestone: 'rhythm-graduate',
    intro: [unitTestCard('the whole rhythm path')],
    parts: [
      strong({ timeSignature: '7/4', grouping: [2, 3, 2], measures: 1, questionCount: 2 }),
      tap({ timeSignature: '4/4', tempo: 84, ...EVERYTHING, questionCount: 2 }),
      tap({ timeSignature: '3/4', tempo: 80, noteValues: QE, triplets: true, questionCount: 2 }),
      tap({ timeSignature: '5/4', grouping: [3, 2], tempo: 76, noteValues: HQE, rests: true, dotted: true, measures: 1, questionCount: 2 }),
      tap({ timeSignature: '6/8', countUnit: 'eighth', tempo: 45, noteValues: QE, rests: true, questionCount: 2 }),
      tap({ timeSignature: '12/8', tempo: 52, noteValues: QE, ties: true, measures: 1, questionCount: 2 }),
      tap({ timeSignature: '2/2', tempo: 72, noteValues: HQE, triplets: true, questionCount: 2 }),
    ],
  },
);

export const RHYTHM_UNITS: readonly UnitDefinition[] = [U1, U2, U3, U4, U5, U6].map((u) => u.unit);
export const RHYTHM_LESSONS: readonly LessonDefinition[] = [U1, U2, U3, U4, U5, U6].flatMap((u) => u.lessons);
